/**
 * RaidSim — one raid on one room, on a fixed timestep.
 *
 * Shape of the thing:
 *  - Rapier owns only what genuinely needs collision response: the floor, the
 *    walls, and the props you can bump into (bricks, swatter posts, fans).
 *  - Every *trap* is analytic. A spring board knows the raider is on it from a
 *    cell lookup, not a sensor callback. Per the studio's rendering rules this
 *    keeps per-step work uniform, but the real reason is authorability: a trap
 *    that fires from a pure function of (cell, time) behaves identically for
 *    the raider, the owner watching a replay, and any future server re-check.
 *  - Nothing here imports three.js. The sim can run headless in tests.
 *
 * State the sim owns: the raider's body and the phase machine (sneaking →
 * carrying → escaped/failed). Everything else about the room is immutable
 * `RoomDef` data.
 */

// Types only — the wasm module itself is fetched on demand (see `initPhysics`).
import type * as RAPIER from '@dimforge/rapier3d-compat'

import type { PropInstance, RoomDef } from '../core/room'
import { ROOM_CONFIG, cellKey, cellToWorld, rotToDir, worldToCell } from '../core/room'
import { propType } from '../core/props'
import { SIM, sweepOffset } from './config'
import type { RaidEvent, RaidInput, RaidPhase, RaiderPose, SimSnapshot } from './types'
import { NEUTRAL_INPUT } from './types'

/**
 * The loaded physics module. Non-null only after `initPhysics()` resolves.
 *
 * Rapier's wasm is by far the heaviest thing we ship (~840 KB gzipped), and the
 * menu and the *builder* need none of it. So it is imported dynamically and
 * awaited only on the way into a raid — a phone opening a share link paints the
 * taunt card while the physics is still arriving.
 */
type RapierApi = (typeof import('@dimforge/rapier3d-compat'))['default']

let R: RapierApi | null = null
let rapierReady: Promise<void> | null = null

/** Load the physics wasm once. Callers must await this before constructing a sim. */
export function initPhysics(): Promise<void> {
  if (!rapierReady) {
    rapierReady = import('@dimforge/rapier3d-compat').then(async (mod) => {
      const api = mod.default
      await api.init()
      R = api
    })
  }
  return rapierReady
}

export function physicsReady(): boolean {
  return R !== null
}

interface FanZone {
  iid: number
  power: number
  cells: number[]
  dx: number
  dz: number
}

interface Swatter {
  iid: number
  period: number
  power: number
  /** Cell the arm sweeps across. */
  cell: number
  /** Facing, used to derive the lateral knock direction. */
  dx: number
  dz: number
}

export interface RaidOptions {
  /** Present only when replaying/spectating; the sim itself never randomises. */
  raidSeconds?: number
}

export class RaidSim {
  readonly room: RoomDef
  private world: RAPIER.World
  private body: RAPIER.RigidBody
  private collider: RAPIER.Collider

  private propsByCell = new Map<number, PropInstance>()
  private fans: FanZone[] = []
  private swatters: Swatter[] = []
  /** Signed sweep offset last step, per swatter — used to detect the arm crossing. */
  private lastSweepSign = new Map<number, number>()
  private cooldownUntil = new Map<number, number>()
  /** Props whose influence the raider was inside last step, for enter-events. */
  private insideLast = new Set<number>()
  private firedIids = new Set<number>()

  private time = 0
  private phase: RaidPhase = 'sneaking'
  private raidRemaining: number
  private escapeRemaining = 0
  private stillTime = 0
  private hesitateArmed = true
  private tumble = 0
  private flung = false
  private yaw = 0
  private grip: number = SIM.raider.groundGrip
  private speedScale = 1
  private pending: RaidEvent[] = []
  private accumulator = 0
  private disposed = false

  constructor(room: RoomDef, opts: RaidOptions = {}) {
    this.room = room
    this.raidRemaining = opts.raidSeconds ?? ROOM_CONFIG.raidSeconds

    this.world = new R!.World({ x: 0, y: SIM.gravity, z: 0 })
    this.world.timestep = SIM.dt

    this.indexProps()
    this.buildFloor()
    this.buildWalls()
    this.buildPropColliders()

    const spawn = cellToWorld(room.door.x, room.door.z, room.width, room.depth)
    const desc = R!.RigidBodyDesc.dynamic()
      .setTranslation(spawn.x, SIM.raider.standY, spawn.z)
      // The figure never physically tumbles: rotation is faked in `tumble` so
      // being launched always reads clearly and never wedges the capsule.
      .lockRotations()
      .setLinearDamping(SIM.raider.linearDamping)
      .setCcdEnabled(true)
    this.body = this.world.createRigidBody(desc)
    this.collider = this.world.createCollider(
      R!.ColliderDesc.capsule(SIM.raider.halfHeight, SIM.raider.radius)
        // The controller owns *all* horizontal motion, so contact friction must
        // be zero — otherwise Rapier's friction fights the grip model and a
        // low-grip surface (marbles) ends up stopping the raider dead, which is
        // the exact opposite of "marbles delete your brakes".
        .setFriction(0)
        .setFrictionCombineRule(R!.CoefficientCombineRule.Min)
        .setRestitution(0.12),
      this.body,
    )
    // Face into the room.
    this.yaw = room.door.z === 0 ? 0 : Math.PI
    this.emit('start')
  }

  // ---------------------------------------------------------------- building

  private indexProps(): void {
    for (const p of this.room.props) {
      this.propsByCell.set(cellKey(p.x, p.z), p)
      const t = propType(p.type)
      const dir = rotToDir(p.rot)
      if (t.behavior.type === 'push') {
        const cells: number[] = []
        for (let i = 1; i <= t.behavior.reach; i++) {
          const nx = p.x + dir.dx * i
          const nz = p.z + dir.dz * i
          if (nx < 0 || nx >= this.room.width || nz < 0 || nz >= this.room.depth) break
          cells.push(cellKey(nx, nz))
        }
        this.fans.push({ iid: p.iid, power: t.behavior.power, cells, dx: dir.dx, dz: dir.dz })
      } else if (t.behavior.type === 'sweep') {
        this.swatters.push({
          iid: p.iid,
          period: t.behavior.period,
          power: t.behavior.power,
          cell: cellKey(p.x + dir.dx, p.z + dir.dz),
          dx: dir.dx,
          dz: dir.dz,
        })
      }
    }
  }

  /**
   * Floor as merged row runs, skipping holes. Row-merging keeps the collider
   * count in the teens instead of one per cell, and a missing run *is* the
   * hole — no sensor, no special case: you simply fall.
   */
  private buildFloor(): void {
    const c = ROOM_CONFIG.cell
    for (let z = 0; z < this.room.depth; z++) {
      let runStart = -1
      for (let x = 0; x <= this.room.width; x++) {
        const prop = x < this.room.width ? this.propsByCell.get(cellKey(x, z)) : undefined
        const isHole = x >= this.room.width || (prop && propType(prop.type).behavior.type === 'pit')
        if (!isHole && runStart < 0) runStart = x
        if (isHole && runStart >= 0) {
          this.addFloorRun(runStart, x - 1, z, c)
          runStart = -1
        }
      }
    }
  }

  private addFloorRun(x0: number, x1: number, z: number, c: number): void {
    const a = cellToWorld(x0, z, this.room.width, this.room.depth)
    const b = cellToWorld(x1, z, this.room.width, this.room.depth)
    const hx = ((x1 - x0 + 1) * c) / 2
    const cx = (a.x + b.x) / 2
    this.world.createCollider(
      R!.ColliderDesc.cuboid(hx, 0.5, c / 2)
        .setTranslation(cx, -0.5, a.z)
        .setFriction(0.9),
    )
  }

  private buildWalls(): void {
    const c = ROOM_CONFIG.cell
    const w = (this.room.width * c) / 2
    const d = (this.room.depth * c) / 2
    const h = SIM.wallHeight / 2
    const t = 0.3
    const walls: Array<[number, number, number, number, number]> = [
      [w + t, h, t, 0, -d - t],
      [w + t, h, t, 0, d + t],
      [t, h, d + t, -w - t, 0],
      [t, h, d + t, w + t, 0],
    ]
    for (const [hx, hy, hz, x, z] of walls) {
      this.world.createCollider(R!.ColliderDesc.cuboid(hx, hy, hz).setTranslation(x, hy, z))
    }
  }

  private buildPropColliders(): void {
    const c = ROOM_CONFIG.cell
    for (const p of this.room.props) {
      const t = propType(p.type)
      if (!t.blocking || t.behavior.type === 'pit') continue
      const w = cellToWorld(p.x, p.z, this.room.width, this.room.depth)
      // Machines are posts, not full cells: you can squeeze past a swatter's
      // body, which is exactly the mistake the arm is waiting for.
      const half = t.kind === 'machine' ? c * 0.22 : c * 0.5
      this.world.createCollider(
        R!.ColliderDesc.cuboid(half, t.height / 2, half)
          .setTranslation(w.x, t.height / 2, w.z)
          .setFriction(0.6),
      )
    }
  }

  // ------------------------------------------------------------------ ticking

  /**
   * Advance by real elapsed time, stepping the sim on its fixed clock.
   * Returns the events produced, which the caller (recorder/UI) drains.
   */
  advance(elapsed: number, input: RaidInput): RaidEvent[] {
    if (this.disposed) return []
    this.accumulator += Math.min(elapsed, SIM.dt * SIM.maxStepsPerFrame)
    let steps = 0
    while (this.accumulator >= SIM.dt && steps < SIM.maxStepsPerFrame) {
      this.step(input)
      this.accumulator -= SIM.dt
      steps++
    }
    const out = this.pending
    this.pending = []
    return out
  }

  /** One fixed step. Public so tests can drive the sim deterministically. */
  step(input: RaidInput = NEUTRAL_INPUT): void {
    if (this.disposed || this.phase === 'escaped' || this.phase === 'failed') return
    const dt = SIM.dt
    this.time += dt

    const pos = this.body.translation()
    const cell = worldToCell(pos.x, pos.z, this.room.width, this.room.depth)

    this.applyFloorModifiers(cell.x, cell.z, pos)
    this.applySprings(cell.x, cell.z, pos)
    this.applySwatters(cell.x, cell.z, pos)
    this.applyFans(cell.x, cell.z, pos)
    this.applyDecoys(pos)
    this.drive(input, pos)

    this.world.step()

    this.postStep(input, dt)
  }

  private grounded(): boolean {
    const p = this.body.translation()
    const origin = { x: p.x, y: p.y - SIM.raider.halfHeight, z: p.z }
    const ray = new R!.Ray(origin, { x: 0, y: -1, z: 0 })
    const hit = this.world.castRay(
      ray,
      SIM.raider.radius + 0.12,
      true,
      undefined,
      undefined,
      this.collider,
      this.body,
    )
    return hit !== null
  }

  private drive(input: RaidInput, pos: RAPIER.Vector): void {
    const v = this.body.linvel()
    const onGround = this.grounded()

    if (this.flung) {
      // Airborne after a launch: only a whisper of control, so the arc stays
      // the owner's joke rather than the raider's correction.
      const air = SIM.raider.airAccel * SIM.dt
      // On the ground they skid, because contact friction is zero by design.
      const skid = onGround ? SIM.raider.skidDecay : 1
      this.body.setLinvel(
        {
          x: (v.x + input.moveX * air) * skid,
          y: v.y,
          z: (v.z + input.moveZ * air) * skid,
        },
        true,
      )
      const horiz = Math.hypot(v.x, v.z)
      if (onGround && horiz < SIM.raider.recoverSpeed) {
        this.flung = false
        this.tumble = 0
      }
      return
    }

    const maxSpeed =
      (this.phase === 'carrying' ? SIM.raider.carrySpeed : SIM.raider.walkSpeed) * this.speedScale
    const wantX = input.moveX * maxSpeed
    const wantZ = input.moveZ * maxSpeed

    if (onGround) {
      const blend = Math.min(1, this.grip * SIM.raider.accel * SIM.dt)
      const nx = v.x + (wantX - v.x) * blend
      const nz = v.z + (wantZ - v.z) * blend
      const ny = input.jump ? SIM.raider.jumpSpeed : v.y
      this.body.setLinvel({ x: nx, y: ny, z: nz }, true)
    } else {
      const air = SIM.raider.airAccel * SIM.dt
      this.body.setLinvel(
        { x: v.x + input.moveX * air, y: v.y, z: v.z + input.moveZ * air },
        true,
      )
    }

    if (Math.hypot(input.moveX, input.moveZ) > 0.08) {
      this.yaw = Math.atan2(input.moveX, input.moveZ)
    }
    void pos
  }

  private applyFloorModifiers(cx: number, cz: number, pos: RAPIER.Vector): void {
    this.grip = SIM.raider.groundGrip
    this.speedScale = 1
    const prop = this.propsByCell.get(cellKey(cx, cz))
    if (!prop) return
    const b = propType(prop.type).behavior
    if (b.type === 'slip') {
      this.grip = b.friction
      this.enterOnce(prop.iid, 'slipped', pos)
    } else if (b.type === 'glue') {
      this.speedScale = b.slow
      this.enterOnce(prop.iid, 'glued', pos)
    } else if (prop.type === 'bluff') {
      // The whole point of the bluff plate: the owner finds out it worked.
      this.enterOnce(prop.iid, 'bluffed', pos)
    }
  }

  private applySprings(cx: number, cz: number, pos: RAPIER.Vector): void {
    const prop = this.propsByCell.get(cellKey(cx, cz))
    if (!prop) return
    const b = propType(prop.type).behavior
    if (b.type !== 'launch') return
    if (!this.ready(prop.iid)) return
    if (!this.grounded()) return
    const dir = rotToDir(prop.rot)
    this.body.setLinvel({ x: dir.dx * b.power, y: b.up, z: dir.dz * b.power }, true)
    this.flung = true
    this.fire(prop.iid, 'launched', pos)
  }

  private applySwatters(cx: number, cz: number, pos: RAPIER.Vector): void {
    const here = cellKey(cx, cz)
    for (const s of this.swatters) {
      const offset = sweepOffset(this.time, s.period)
      const sign = offset >= 0 ? 1 : -1
      const prevSign = this.lastSweepSign.get(s.iid) ?? sign
      this.lastSweepSign.set(s.iid, sign)
      if (s.cell !== here) continue
      // The arm connects as it crosses the middle of its cell.
      if (sign === prevSign) continue
      if (!this.ready(s.iid)) continue
      // Knock perpendicular to the swatter's facing, in the direction of travel.
      const lateral = { x: -s.dz, z: s.dx }
      const swing = sign > 0 ? 1 : -1
      this.body.setLinvel(
        {
          x: lateral.x * s.power * swing,
          y: 4.6,
          z: lateral.z * s.power * swing,
        },
        true,
      )
      this.flung = true
      this.fire(s.iid, 'swatted', pos)
    }
  }

  private applyFans(cx: number, cz: number, pos: RAPIER.Vector): void {
    const here = cellKey(cx, cz)
    for (const f of this.fans) {
      const idx = f.cells.indexOf(here)
      if (idx < 0) continue
      const falloff = 1 - idx / (f.cells.length + 1)
      const v = this.body.linvel()
      const push = f.power * falloff * SIM.dt
      this.body.setLinvel({ x: v.x + f.dx * push, y: v.y, z: v.z + f.dz * push }, true)
      this.enterOnce(f.iid, 'pushed', pos)
    }
  }

  private applyDecoys(pos: RAPIER.Vector): void {
    for (const p of this.room.props) {
      if (p.type !== 'decoy') continue
      const w = cellToWorld(p.x, p.z, this.room.width, this.room.depth)
      const near = Math.hypot(pos.x - w.x, pos.z - w.z) < SIM.decoyRadius
      if (near) this.enterOnce(p.iid, 'decoy', pos)
      else this.insideLast.delete(p.iid)
    }
  }

  private postStep(input: RaidInput, dt: number): void {
    const pos = this.body.translation()
    const v = this.body.linvel()

    if (this.flung) {
      this.tumble += (Math.hypot(v.x, v.y, v.z) * 0.9 + 3) * dt
    }

    // Fell through a hole.
    if (pos.y < SIM.fallY) {
      this.pitfall(pos)
      return
    }

    // Loot.
    const chest = cellToWorld(this.room.chest.x, this.room.chest.z, this.room.width, this.room.depth)
    if (this.phase === 'sneaking' && Math.hypot(pos.x - chest.x, pos.z - chest.z) < SIM.pickupRadius) {
      this.phase = 'carrying'
      this.escapeRemaining = ROOM_CONFIG.escapeSeconds
      this.emit('pickup', pos)
    }

    // Exit.
    const door = cellToWorld(this.room.door.x, this.room.door.z, this.room.width, this.room.depth)
    if (this.phase === 'carrying') {
      this.escapeRemaining -= dt
      if (Math.hypot(pos.x - door.x, pos.z - door.z) < SIM.escapeRadius) {
        this.phase = 'escaped'
        this.emit('escaped', pos)
        return
      }
      if (this.escapeRemaining <= 0) {
        this.phase = 'failed'
        this.emit('failed', pos)
        return
      }
    }

    this.raidRemaining -= dt
    if (this.raidRemaining <= 0) {
      this.phase = 'failed'
      this.emit('failed', pos)
      return
    }

    // Hesitation: the single most useful thing the owner can learn. One event
    // per stand-still episode, so a cautious player doesn't spam the timeline.
    const moving = Math.hypot(input.moveX, input.moveZ) > 0.1
    if (moving || this.flung) {
      this.stillTime = 0
      this.hesitateArmed = true
    } else {
      this.stillTime += dt
      if (this.hesitateArmed && this.stillTime >= SIM.hesitateSeconds) {
        this.hesitateArmed = false
        this.emit('hesitate', pos)
      }
    }
  }

  private pitfall(pos: RAPIER.Vector): void {
    const door = cellToWorld(this.room.door.x, this.room.door.z, this.room.width, this.room.depth)
    this.body.setTranslation({ x: door.x, y: SIM.raider.standY, z: door.z }, true)
    this.body.setLinvel({ x: 0, y: 0, z: 0 }, true)
    this.flung = false
    this.tumble = 0
    this.insideLast.clear()
    if (this.phase === 'carrying' && SIM.pitfallDropsChest) {
      this.phase = 'sneaking'
      this.escapeRemaining = 0
    }
    this.emit('pitfall', pos)
  }

  // ------------------------------------------------------------------- events

  private ready(iid: number): boolean {
    const until = this.cooldownUntil.get(iid) ?? -1
    return this.time >= until
  }

  private fire(iid: number, type: RaidEvent['type'], pos: RAPIER.Vector): void {
    this.cooldownUntil.set(iid, this.time + SIM.trapCooldown)
    this.firedIids.add(iid)
    this.emit(type, pos, iid)
  }

  /** Emit once when the raider enters a prop's zone, not every step inside it. */
  private enterOnce(iid: number, type: RaidEvent['type'], pos: RAPIER.Vector): void {
    if (this.insideLast.has(iid)) return
    this.insideLast.add(iid)
    this.firedIids.add(iid)
    this.emit(type, pos, iid)
  }

  private emit(type: RaidEvent['type'], pos?: RAPIER.Vector, iid?: number): void {
    const p = pos ?? this.body?.translation() ?? { x: 0, y: 0, z: 0 }
    const ev: RaidEvent = { t: this.time, type, x: p.x, z: p.z }
    if (iid !== undefined) ev.iid = iid
    this.pending.push(ev)
  }

  // ----------------------------------------------------------------- readback

  snapshot(): SimSnapshot {
    const p = this.body.translation()
    const v = this.body.linvel()
    const speed = Math.hypot(v.x, v.z)
    const onGround = this.grounded()
    let pose: RaiderPose = 'stand'
    if (this.flung) pose = 'flung'
    else if (!onGround) pose = 'air'
    else if (this.speedScale < 1) pose = 'stuck'
    else if (speed > 0.6) pose = 'run'
    const cell = worldToCell(p.x, p.z, this.room.width, this.room.depth)
    return {
      time: this.time,
      phase: this.phase,
      raider: {
        x: p.x,
        y: p.y,
        z: p.z,
        yaw: this.yaw,
        tumble: this.tumble,
        vx: v.x,
        vy: v.y,
        vz: v.z,
        speed,
        grounded: onGround,
        pose,
        carrying: this.phase === 'carrying',
      },
      escapeRemaining: Math.max(0, this.escapeRemaining),
      raidRemaining: Math.max(0, this.raidRemaining),
      cellX: cell.x,
      cellZ: cell.z,
      firedIids: [...this.firedIids],
    }
  }

  get finished(): boolean {
    return this.phase === 'escaped' || this.phase === 'failed'
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.world.free()
  }
}
